import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Plus,
  Trash2,
  Link,
  Filter,
  ArrowRight,
  Play,
  Code,
  Eye,
  ChevronDown,
  ChevronRight,
  Database,
  Columns,
  GitBranch,
  Download,
  Copy,
  SortAsc,
  Group,
  Sigma,
  Layers,
} from "lucide-react";
import Button from "./ui/Button";
import { toast } from "react-toastify";
import { api } from "../common/api";

interface Column {
  name: string;
  type: string;
  table: string;
  selected: boolean;
  aggregation?: "SUM" | "AVG" | "COUNT" | "MIN" | "MAX" | "NONE";
  alias?: string;
}

interface TableNode {
  id: string;
  name: string;
  alias?: string;
  columns: Column[];
  position: { x: number; y: number };
}

interface Join {
  id: string;
  leftTable: string;
  rightTable: string;
  leftColumn: string;
  rightColumn: string;
  type: "INNER" | "LEFT" | "RIGHT" | "FULL";
}

interface Condition {
  id: string;
  column: string;
  operator: string;
  value: string;
  logical: "AND" | "OR";
}

interface QueryResult {
  columns: Array<{ name: string; type: string }>;
  data: any[];
  rowCount: number;
  executionTime: number;
}

interface VisualQueryBuilderProps {
  onQueryChange?: (sql: string) => void;
  selectedConnection?: {
    id: string;
    connection_id?: string;
    name: string;
    type: string;
  };
}

interface OrderBy {
  id: string;
  column: string;
  direction: "ASC" | "DESC";
}

interface GroupBy {
  id: string;
  column: string;
}

interface Having {
  id: string;
  aggregation: string;
  operator: string;
  value: string;
  logical: "AND" | "OR";
}

const VisualQueryBuilder: React.FC<VisualQueryBuilderProps> = ({
  onQueryChange,
  selectedConnection,
}) => {
  const [tables, setTables] = useState<TableNode[]>([]);
  const [joins, setJoins] = useState<Join[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [availableTables, setAvailableTables] = useState<string[]>([]);
  const [showTableSelector, setShowTableSelector] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showConditionDialog, setShowConditionDialog] = useState(false);
  const [generatedSQL, setGeneratedSQL] = useState("");
  const [showSQLPreview, setShowSQLPreview] = useState(true);
  const [limit, setLimit] = useState<number>(100);
  const [distinct, setDistinct] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [tableSchemas, setTableSchemas] = useState<Record<string, Column[]>>(
    {}
  );
  const [orderBy, setOrderBy] = useState<OrderBy[]>([]);
  const [groupBy, setGroupBy] = useState<GroupBy[]>([]);
  const [having, setHaving] = useState<Having[]>([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showHavingDialog, setShowHavingDialog] = useState(false);
  const [showAggregationPanel, setShowAggregationPanel] = useState(false);

  // Fetch available tables from the connection
  useEffect(() => {
    if (selectedConnection) {
      fetchAvailableTables();
    }
  }, [selectedConnection]);

  const fetchAvailableTables = async () => {
    if (!selectedConnection) return;

    setIsLoadingTables(true);
    try {
      const connectionId =
        selectedConnection.connection_id || selectedConnection.id;

      // Get auth token
      const tokens = localStorage.getItem("token");
      if (tokens) {
        const parsedTokens = JSON.parse(tokens);
        const accessToken = parsedTokens.accessToken;
        if (accessToken) {
          api.setAuthToken(accessToken);
        }
      }

      // Fetch schema information
      const response = await api.extractSchema({
        connection_id: connectionId,
      });

      console.log("Schema API Response:", response); // Debug log

      // Handle different response structures
      let tableNames: string[] = [];
      const schemas: Record<string, Column[]> = {};

      if (response && response.tables) {
        // Check if tables is an object with table names as keys
        if (
          typeof response.tables === "object" &&
          !Array.isArray(response.tables)
        ) {
          tableNames = Object.keys(response.tables);

          // Extract columns for each table
          Object.entries(response.tables).forEach(
            ([tableName, tableInfo]: [string, any]) => {
              const columns: Column[] = [];

              if (tableInfo.columns) {
                // Handle columns as object
                if (
                  typeof tableInfo.columns === "object" &&
                  !Array.isArray(tableInfo.columns)
                ) {
                  Object.entries(tableInfo.columns).forEach(
                    ([colName, colInfo]: [string, any]) => {
                      columns.push({
                        name: colName,
                        type: colInfo?.type || colInfo?.data_type || "unknown",
                        table: tableName,
                        selected: false,
                      });
                    }
                  );
                }
                // Handle columns as array
                else if (Array.isArray(tableInfo.columns)) {
                  tableInfo.columns.forEach((col: any) => {
                    columns.push({
                      name: col.name || col.column_name,
                      type: col.type || col.data_type || "unknown",
                      table: tableName,
                      selected: false,
                    });
                  });
                }
              }

              if (columns.length > 0) {
                schemas[tableName] = columns;
              }
            }
          );
        }
        // Check if tables is an array
        else if (Array.isArray(response.tables)) {
          tableNames = response.tables.map(
            (t: any) => t.name || t.table_name || t
          );

          // If array contains objects with schema info
          response.tables.forEach((tableInfo: any) => {
            const tableName = tableInfo.name || tableInfo.table_name;
            if (tableName && tableInfo.columns) {
              const columns: Column[] = [];

              if (Array.isArray(tableInfo.columns)) {
                tableInfo.columns.forEach((col: any) => {
                  columns.push({
                    name: col.name || col.column_name,
                    type: col.type || col.data_type || "unknown",
                    table: tableName,
                    selected: false,
                  });
                });
              }

              if (columns.length > 0) {
                schemas[tableName] = columns;
              }
            }
          });
        }
      }
      // Handle alternative response format where tables might be at root level
      else if (response && Array.isArray(response)) {
        tableNames = response.map((t: any) => t.name || t.table_name || t);
      }

      console.log("Parsed table names:", tableNames); // Debug log
      console.log("Parsed schemas:", schemas); // Debug log

      if (tableNames.length > 0) {
        setAvailableTables(tableNames);
        setTableSchemas(schemas);
        toast.success(`Loaded ${tableNames.length} tables from database`);
      } else {
        toast.error("No tables found in the database");
        // Fallback: Try to execute a query to get table names
        await fetchTablesFallback(connectionId);
      }
    } catch (error: any) {
      console.error("Failed to fetch tables:", error);
      toast.error("Failed to load tables from database.");

      // Try fallback method
      const connectionId =
        selectedConnection.connection_id || selectedConnection.id;
      await fetchTablesFallback(connectionId);
    } finally {
      setIsLoadingTables(false);
    }
  };

  const fetchTablesFallback = async (connectionId: string) => {
    try {
      // Try to query information_schema or pg_tables to get table names
      let query = "";

      // Use appropriate query based on database type
      if (selectedConnection?.type?.toLowerCase().includes("postgres")) {
        query = `SELECT table_name FROM information_schema.tables WHERE table_schema = 'public' ORDER BY table_name;`;
      } else if (selectedConnection?.type?.toLowerCase().includes("mysql")) {
        query = `SELECT table_name FROM information_schema.tables WHERE table_schema = DATABASE() ORDER BY table_name;`;
      } else if (
        selectedConnection?.type?.toLowerCase().includes("mssql") ||
        selectedConnection?.type?.toLowerCase().includes("sqlserver")
      ) {
        query = `SELECT table_name FROM information_schema.tables WHERE table_type = 'BASE TABLE' ORDER BY table_name;`;
      } else {
        // Generic fallback
        query = `SELECT table_name FROM information_schema.tables ORDER BY table_name;`;
      }

      const response = await api.executeQuery({
        connection_id: connectionId,
        sql: query,
      });

      if (response && response.rows && response.rows.length > 0) {
        const tableNames = response.rows.map(
          (row: any) =>
            row.table_name || row.TABLE_NAME || Object.values(row)[0]
        );

        console.log("Tables from fallback query:", tableNames); // Debug log

        setAvailableTables(tableNames);
        toast.success(
          `Loaded ${tableNames.length} tables from database (fallback method)`
        );

        // Fetch columns for each table
        await fetchColumnsForTables(connectionId, tableNames);
      }
    } catch (fallbackError) {
      console.error("Fallback fetch also failed:", fallbackError);
      toast.error(
        "Could not retrieve table information. Please check your database connection."
      );
    }
  };

  const fetchColumnsForTables = async (
    connectionId: string,
    tableNames: string[]
  ) => {
    const schemas: Record<string, Column[]> = {};

    for (const tableName of tableNames.slice(0, 10)) {
      // Limit to first 10 tables for performance
      try {
        let query = "";

        if (selectedConnection?.type?.toLowerCase().includes("postgres")) {
          query = `SELECT column_name, data_type FROM information_schema.columns WHERE table_name = '${tableName}' AND table_schema = 'public';`;
        } else if (selectedConnection?.type?.toLowerCase().includes("mysql")) {
          query = `SELECT column_name, data_type FROM information_schema.columns WHERE table_name = '${tableName}' AND table_schema = DATABASE();`;
        } else {
          query = `SELECT column_name, data_type FROM information_schema.columns WHERE table_name = '${tableName}';`;
        }

        const response = await api.executeQuery({
          connection_id: connectionId,
          sql: query,
        });

        if (response && response.rows && response.rows.length > 0) {
          schemas[tableName] = response.rows.map((row: any) => ({
            name: row.column_name || row.COLUMN_NAME,
            type: row.data_type || row.DATA_TYPE || "unknown",
            table: tableName,
            selected: false,
          }));
        }
      } catch (error) {
        console.error(`Failed to fetch columns for table ${tableName}:`, error);
      }
    }

    setTableSchemas(schemas);
  };

  // Generate SQL whenever builder state changes
  useEffect(() => {
    const sql = generateSQL();
    setGeneratedSQL(sql);
    if (onQueryChange) {
      onQueryChange(sql);
    }
  }, [tables, joins, conditions, limit, distinct, orderBy, groupBy, having]);

  const generateSQL = useCallback((): string => {
    if (tables.length === 0) return "";

    // SELECT clause with aggregations
    const selectedColumns = tables.flatMap((table) =>
      table.columns
        .filter((col) => col.selected)
        .map((col) => {
          const tableRef = table.alias || table.name;
          const columnRef = `${tableRef}.${col.name}`;

          if (col.aggregation && col.aggregation !== "NONE") {
            const aggColumn = `${col.aggregation}(${columnRef})`;
            return col.alias ? `${aggColumn} AS ${col.alias}` : aggColumn;
          }

          return col.alias ? `${columnRef} AS ${col.alias}` : columnRef;
        })
    );

    if (selectedColumns.length === 0) {
      selectedColumns.push("*");
    }

    let sql = `SELECT ${distinct ? "DISTINCT " : ""}${selectedColumns.join(
      ", "
    )}`;

    // FROM clause. Tables reached by an explicit JOIN are emitted below; any
    // other table the user pulled columns from must still be declared here or
    // its alias is unresolved and the query fails.
    const joinedTableNames = new Set(joins.map((j) => j.rightTable));
    const fromTables = tables.filter(
      (t, idx) => idx === 0 || !joinedTableNames.has(t.name)
    );
    sql += `\nFROM ${fromTables
      .map((t) => `${t.name}${t.alias ? ` AS ${t.alias}` : ""}`)
      .join(", ")}`;

    // JOIN clauses. Reference aliases, not raw table names: once a table is
    // aliased the bare name is no longer a valid qualifier.
    const refFor = (tableName: string) => {
      const t = tables.find((tb) => tb.name === tableName);
      return t?.alias || tableName;
    };
    joins.forEach((join) => {
      const rightTable = tables.find((t) => t.name === join.rightTable);
      sql += `\n${join.type} JOIN ${join.rightTable}${
        rightTable?.alias ? ` AS ${rightTable.alias}` : ""
      } ON ${refFor(join.leftTable)}.${join.leftColumn} = ${refFor(
        join.rightTable
      )}.${join.rightColumn}`;
    });

    // WHERE clause
    if (conditions.length > 0) {
      sql += "\nWHERE ";
      sql += conditions
        .map((cond, idx) => {
          const prefix = idx > 0 ? ` ${cond.logical} ` : "";
          return `${prefix}${cond.column} ${cond.operator} ${cond.value}`;
        })
        .join("");
    }

    // GROUP BY clause
    if (groupBy.length > 0) {
      sql += `\nGROUP BY ${groupBy.map((g) => g.column).join(", ")}`;
    }

    // HAVING clause
    if (having.length > 0) {
      sql += "\nHAVING ";
      sql += having
        .map((h, idx) => {
          const prefix = idx > 0 ? ` ${h.logical} ` : "";
          return `${prefix}${h.aggregation} ${h.operator} ${h.value}`;
        })
        .join("");
    }

    // ORDER BY clause
    if (orderBy.length > 0) {
      sql += `\nORDER BY ${orderBy
        .map((o) => `${o.column} ${o.direction}`)
        .join(", ")}`;
    }

    // LIMIT clause
    if (limit > 0) {
      sql += `\nLIMIT ${limit}`;
    }

    return sql + ";";
  }, [tables, joins, conditions, limit, distinct, orderBy, groupBy, having]);

  const executeQuery = async () => {
    if (!generatedSQL || !selectedConnection) {
      toast.error(
        "Please build a query and ensure a database connection is selected"
      );
      return;
    }

    setIsExecuting(true);
    setError(null);
    setQueryResult(null);

    try {
      const connectionId =
        selectedConnection.connection_id || selectedConnection.id;

      // Get auth token
      const tokens = localStorage.getItem("token");
      if (tokens) {
        const parsedTokens = JSON.parse(tokens);
        const accessToken = parsedTokens.accessToken;
        if (accessToken) {
          api.setAuthToken(accessToken);
        }
      }

      const response = await api.executeQuery({
        connection_id: connectionId,
        sql: generatedSQL,
      });

      const result: QueryResult = {
        columns: response.columns || [],
        data: response.rows || [],
        rowCount: response.row_count || 0,
        executionTime: response.execution_time_ms || 0,
      };

      setQueryResult(result);
      toast.success(
        `Query executed successfully! ${result.rowCount} rows returned in ${result.executionTime}ms`
      );
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to execute query";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Query execution error:", error);
    } finally {
      setIsExecuting(false);
    }
  };

  const copySQL = () => {
    navigator.clipboard.writeText(generatedSQL);
    toast.success("SQL copied to clipboard");
  };

  const addTable = (tableName: string) => {
    const columns = tableSchemas[tableName] || getMockColumns(tableName);

    const newTable: TableNode = {
      id: `table_${Date.now()}`,
      name: tableName,
      alias: `t${tables.length + 1}`,
      columns: columns.map((col) => ({ ...col, selected: false })),
      position: { x: 50 + tables.length * 250, y: 50 },
    };
    setTables([...tables, newTable]);
    setShowTableSelector(false);
    toast.success(`Added table: ${tableName}`);
  };

  const removeTable = (tableId: string) => {
    const table = tables.find((t) => t.id === tableId);
    setTables(tables.filter((t) => t.id !== tableId));
    setJoins(
      joins.filter(
        (j) => j.leftTable !== table?.name && j.rightTable !== table?.name
      )
    );
    toast.info(`Removed table: ${table?.name}`);
  };

  const toggleColumn = (tableId: string, columnName: string) => {
    setTables(
      tables.map((table) =>
        table.id === tableId
          ? {
              ...table,
              columns: table.columns.map((col) =>
                col.name === columnName
                  ? { ...col, selected: !col.selected }
                  : col
              ),
            }
          : table
      )
    );
  };

  const selectAllColumns = (tableId: string) => {
    setTables(
      tables.map((table) =>
        table.id === tableId
          ? {
              ...table,
              columns: table.columns.map((col) => ({ ...col, selected: true })),
            }
          : table
      )
    );
  };

  const deselectAllColumns = (tableId: string) => {
    setTables(
      tables.map((table) =>
        table.id === tableId
          ? {
              ...table,
              columns: table.columns.map((col) => ({
                ...col,
                selected: false,
              })),
            }
          : table
      )
    );
  };

  const addJoin = (join: Omit<Join, "id">) => {
    const newJoin: Join = {
      ...join,
      id: `join_${Date.now()}`,
    };
    setJoins([...joins, newJoin]);
    setShowJoinDialog(false);
    toast.success("Join added successfully");
  };

  const removeJoin = (joinId: string) => {
    setJoins(joins.filter((j) => j.id !== joinId));
    toast.info("Join removed");
  };

  const addCondition = (condition: Omit<Condition, "id">) => {
    const newCondition: Condition = {
      ...condition,
      id: `condition_${Date.now()}`,
    };
    setConditions([...conditions, newCondition]);
    setShowConditionDialog(false);
    toast.success("Condition added successfully");
  };

  const removeCondition = (conditionId: string) => {
    setConditions(conditions.filter((c) => c.id !== conditionId));
    toast.info("Condition removed");
  };

  const addOrderBy = (orderBy: Omit<OrderBy, "id">) => {
    const newOrder: OrderBy = {
      ...orderBy,
      id: `order_${Date.now()}`,
    };
    setOrderBy((prev) => [...prev, newOrder]);
    setShowOrderDialog(false);
    toast.success("Order by added successfully");
  };

  const removeOrderBy = (id: string) => {
    setOrderBy((prev) => prev.filter((o) => o.id !== id));
    toast.info("Order by removed");
  };

  const addGroupBy = (groupBy: Omit<GroupBy, "id">) => {
    const newGroup: GroupBy = {
      ...groupBy,
      id: `group_${Date.now()}`,
    };
    setGroupBy((prev) => [...prev, newGroup]);
    setShowGroupDialog(false);
    toast.success("Group by added successfully");
  };

  const removeGroupBy = (id: string) => {
    setGroupBy((prev) => prev.filter((g) => g.id !== id));
    toast.info("Group by removed");
  };

  const addHaving = (having: Omit<Having, "id">) => {
    const newHaving: Having = {
      ...having,
      id: `having_${Date.now()}`,
    };
    setHaving((prev) => [...prev, newHaving]);
    setShowHavingDialog(false);
    toast.success("Having clause added successfully");
  };

  const removeHaving = (id: string) => {
    setHaving((prev) => prev.filter((h) => h.id !== id));
    toast.info("Having clause removed");
  };

  const clearAll = () => {
    setTables([]);
    setJoins([]);
    setConditions([]);
    setOrderBy([]);
    setGroupBy([]);
    setHaving([]);
    setQueryResult(null);
    setError(null);
    toast.info("Query builder cleared");
  };

  const toggleColumnAggregation = (
    tableId: string,
    columnName: string,
    aggregation: Column["aggregation"]
  ) => {
    setTables(
      tables.map((table) =>
        table.id === tableId
          ? {
              ...table,
              columns: table.columns.map((col) =>
                col.name === columnName
                  ? { ...col, aggregation: aggregation || "NONE" }
                  : col
              ),
            }
          : table
      )
    );
  };

  const setColumnAlias = (
    tableId: string,
    columnName: string,
    alias: string
  ) => {
    setTables(
      tables.map((table) =>
        table.id === tableId
          ? {
              ...table,
              columns: table.columns.map((col) =>
                col.name === columnName ? { ...col, alias } : col
              ),
            }
          : table
      )
    );
  };

  // Mock function to get columns for a table (fallback only)
  const getMockColumns = (tableName: string): Column[] => {
    return [
      { name: "id", type: "integer", table: tableName, selected: false },
      { name: "name", type: "varchar", table: tableName, selected: false },
      {
        name: "created_at",
        type: "timestamp",
        table: tableName,
        selected: false,
      },
    ];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold">Visual Query Builder</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={clearAll}
              disabled={tables.length === 0}
            >
              Clear All
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowSQLPreview(!showSQLPreview)}
              icon={
                showSQLPreview ? (
                  <Eye className="w-4 h-4" />
                ) : (
                  <Code className="w-4 h-4" />
                )
              }
            >
              {showSQLPreview ? "Hide" : "Show"} SQL
            </Button>
          </div>
        </div>

        {/* Connection Info */}
        {selectedConnection ? (
          <div className="bg-gray-700 rounded-lg p-3 flex items-center gap-3">
            <Database className="w-4 h-4 text-green-400" />
            <span className="text-sm">
              Connected to: <strong>{selectedConnection.name}</strong> (
              {selectedConnection.type})
            </span>
            {isLoadingTables && (
              <span className="text-xs text-gray-400 ml-2">
                Loading tables...
              </span>
            )}
          </div>
        ) : (
          <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-3">
            <Database className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-yellow-400">
              Please select a database connection to use the visual query
              builder
            </span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowTableSelector(true)}
            disabled={!selectedConnection || isLoadingTables}
            icon={<Table className="w-4 h-4" />}
          >
            Add Table
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowJoinDialog(true)}
            disabled={tables.length < 2}
            icon={<Link className="w-4 h-4" />}
          >
            Join
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowConditionDialog(true)}
            disabled={tables.length === 0}
            icon={<Filter className="w-4 h-4" />}
          >
            Filter
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowGroupDialog(true)}
            disabled={tables.length === 0}
            icon={<Group className="w-4 h-4" />}
          >
            Group
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowOrderDialog(true)}
            disabled={tables.length === 0}
            icon={<SortAsc className="w-4 h-4" />}
          >
            Order
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAggregationPanel(!showAggregationPanel)}
            disabled={tables.length === 0}
            icon={<Sigma className="w-4 h-4" />}
          >
            Aggregate
          </Button>
          <div className="ml-auto flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={distinct}
                onChange={(e) => setDistinct(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">DISTINCT</span>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-sm">Limit:</span>
              <input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
                className="w-20 bg-gray-700 rounded px-2 py-1 text-sm"
                min="0"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Aggregation Panel */}
      {showAggregationPanel && tables.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sigma className="w-5 h-5 text-orange-400" />
            Column Aggregations & Aliases
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tables.flatMap((table) =>
              table.columns
                .filter((col) => col.selected)
                .map((col) => (
                  <div
                    key={`${table.id}-${col.name}`}
                    className="bg-gray-700 p-3 rounded-lg space-y-2"
                  >
                    <div className="text-sm font-medium">
                      {table.name}.{col.name}
                    </div>
                    <div className="space-y-2">
                      <select
                        value={col.aggregation || "NONE"}
                        onChange={(e) =>
                          toggleColumnAggregation(
                            table.id,
                            col.name,
                            e.target.value as Column["aggregation"]
                          )
                        }
                        className="w-full bg-gray-600 rounded px-2 py-1 text-sm"
                      >
                        <option value="NONE">No Aggregation</option>
                        <option value="SUM">SUM</option>
                        <option value="AVG">AVG</option>
                        <option value="COUNT">COUNT</option>
                        <option value="MIN">MIN</option>
                        <option value="MAX">MAX</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Alias (optional)"
                        value={col.alias || ""}
                        onChange={(e) =>
                          setColumnAlias(table.id, col.name, e.target.value)
                        }
                        className="w-full bg-gray-600 rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}

      {/* Tables Canvas */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {tables.map((table) => (
            <TableCard
              key={table.id}
              table={table}
              onRemove={() => removeTable(table.id)}
              onToggleColumn={(columnName) =>
                toggleColumn(table.id, columnName)
              }
              onSelectAll={() => selectAllColumns(table.id)}
              onDeselectAll={() => deselectAllColumns(table.id)}
            />
          ))}
          {tables.length === 0 && (
            <div className="col-span-3 text-center py-16 bg-gray-700 rounded-lg">
              <Database className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">No tables added yet</p>
              {!selectedConnection && (
                <p className="text-sm text-gray-500 mb-4">
                  Please connect to a database first
                </p>
              )}
              {selectedConnection &&
                availableTables.length === 0 &&
                !isLoadingTables && (
                  <p className="text-sm text-gray-500 mb-4">
                    No tables available in the connected database
                  </p>
                )}
              {selectedConnection && availableTables.length > 0 && (
                <Button
                  variant="primary"
                  onClick={() => setShowTableSelector(true)}
                  icon={<Plus className="w-4 h-4" />}
                >
                  Add Your First Table
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Joins Section */}
      {joins.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Link className="w-5 h-5 text-green-400" />
            Joins ({joins.length})
          </h3>
          <div className="space-y-2">
            {joins.map((join) => (
              <JoinCard
                key={join.id}
                join={join}
                onRemove={() => removeJoin(join.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Conditions Section */}
      {conditions.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5 text-purple-400" />
            Conditions ({conditions.length})
          </h3>
          <div className="space-y-2">
            {conditions.map((condition) => (
              <ConditionCard
                key={condition.id}
                condition={condition}
                onRemove={() => removeCondition(condition.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Group By Section */}
      {groupBy.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Group className="w-5 h-5 text-blue-400" />
            Group By ({groupBy.length})
          </h3>
          <div className="space-y-2">
            {groupBy.map((group) => (
              <div
                key={group.id}
                className="bg-gray-700 p-3 rounded-lg flex items-center justify-between hover:bg-gray-600 transition-colors"
              >
                <span className="text-sm font-mono">{group.column}</span>
                <button
                  onClick={() => removeGroupBy(group.id)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Having Section */}
      {having.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5 text-yellow-400" />
            Having ({having.length})
          </h3>
          <div className="space-y-2">
            {having.map((h) => (
              <div
                key={h.id}
                className="bg-gray-700 p-3 rounded-lg flex items-center justify-between hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {h.logical && (
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs font-semibold">
                      {h.logical}
                    </span>
                  )}
                  <span className="text-sm font-mono">
                    {h.aggregation} {h.operator} {h.value}
                  </span>
                </div>
                <button
                  onClick={() => removeHaving(h.id)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order By Section */}
      {orderBy.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <SortAsc className="w-5 h-5 text-cyan-400" />
            Order By ({orderBy.length})
          </h3>
          <div className="space-y-2">
            {orderBy.map((order) => (
              <div
                key={order.id}
                className="bg-gray-700 p-3 rounded-lg flex items-center justify-between hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono">{order.column}</span>
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      order.direction === "ASC"
                        ? "bg-cyan-500/20 text-cyan-400"
                        : "bg-orange-500/20 text-orange-400"
                    }`}
                  >
                    {order.direction}
                  </span>
                </div>
                <button
                  onClick={() => removeOrderBy(order.id)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SQL Preview */}
      {showSQLPreview && generatedSQL && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Code className="w-5 h-5 text-blue-400" />
              Generated SQL
            </h3>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={copySQL}
                icon={<Copy className="w-4 h-4" />}
              >
                Copy
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={executeQuery}
                loading={isExecuting}
                loadingText="Executing..."
                disabled={!generatedSQL || !selectedConnection}
                icon={<Play className="w-4 h-4" />}
              >
                Execute Query
              </Button>
            </div>
          </div>
          <pre className="bg-gray-900 p-4 rounded-lg text-green-400 text-sm overflow-x-auto font-mono">
            {generatedSQL}
          </pre>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Query Results */}
      {queryResult && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Query Results</h3>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>{queryResult.rowCount} rows</span>
              <span>•</span>
              <span>{queryResult.executionTime}ms</span>
              <Button
                variant="secondary"
                size="sm"
                icon={<Download className="w-4 h-4" />}
              >
                Export
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  {queryResult.columns.map((column, idx) => (
                    <th key={idx} className="text-left py-2 px-3 font-medium">
                      <div className="flex flex-col">
                        <span>{column.name}</span>
                        <span className="text-xs text-gray-500 font-normal">
                          {column.type}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {queryResult.data.map((row, rowIdx) => (
                  <tr
                    key={rowIdx}
                    className="border-b border-gray-700 hover:bg-gray-700"
                  >
                    {queryResult.columns.map((column, colIdx) => {
                      const value = row[column.name];
                      return (
                        <td key={colIdx} className="py-2 px-3">
                          {value === null || value === undefined ? (
                            <span className="text-gray-500 italic">null</span>
                          ) : typeof value === "object" ? (
                            <span className="text-blue-400">
                              {JSON.stringify(value)}
                            </span>
                          ) : typeof value === "boolean" ? (
                            <span
                              className={
                                value ? "text-green-400" : "text-red-400"
                              }
                            >
                              {String(value)}
                            </span>
                          ) : typeof value === "number" ? (
                            <span className="text-yellow-400">{value}</span>
                          ) : (
                            String(value)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialogs */}
      {showTableSelector && (
        <TableSelectorDialog
          tables={availableTables}
          onSelect={addTable}
          onClose={() => setShowTableSelector(false)}
        />
      )}
      {showJoinDialog && (
        <JoinDialog
          tables={tables}
          onAdd={addJoin}
          onClose={() => setShowJoinDialog(false)}
        />
      )}
      {showConditionDialog && (
        <ConditionDialog
          tables={tables}
          onAdd={addCondition}
          onClose={() => setShowConditionDialog(false)}
        />
      )}
      {showOrderDialog && (
        <OrderByDialog
          tables={tables}
          onAdd={addOrderBy}
          onClose={() => setShowOrderDialog(false)}
        />
      )}
      {showGroupDialog && (
        <GroupByDialog
          tables={tables}
          onAdd={addGroupBy}
          onClose={() => setShowGroupDialog(false)}
        />
      )}
      {showHavingDialog && (
        <HavingDialog
          onAdd={addHaving}
          onClose={() => setShowHavingDialog(false)}
        />
      )}
    </div>
  );
};

// Supporting Components

const TableCard: React.FC<{
  table: TableNode;
  onRemove: () => void;
  onToggleColumn: (columnName: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}> = ({ table, onRemove, onToggleColumn, onSelectAll, onDeselectAll }) => {
  const [expanded, setExpanded] = useState(true);
  const selectedCount = table.columns.filter((c) => c.selected).length;

  return (
    <div className="bg-gray-700 rounded-lg overflow-hidden border border-gray-600 hover:border-gray-500 transition-colors">
      <div className="bg-gray-600 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="hover:text-blue-400"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <Table className="w-4 h-4 text-blue-400" />
          <span className="font-semibold">{table.name}</span>
          {table.alias && (
            <span className="text-xs text-gray-400">({table.alias})</span>
          )}
          <span className="text-xs text-gray-400 ml-auto mr-2">
            {selectedCount}/{table.columns.length}
          </span>
        </div>
        <button
          onClick={onRemove}
          className="text-red-400 hover:text-red-300 transition-colors p-1"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {expanded && (
        <div className="p-2">
          <div className="flex gap-2 mb-2 px-2">
            <button
              onClick={onSelectAll}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Select All
            </button>
            <span className="text-gray-600">|</span>
            <button
              onClick={onDeselectAll}
              className="text-xs text-gray-400 hover:text-gray-300"
            >
              Deselect All
            </button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {table.columns.map((column) => (
              <label
                key={column.name}
                className="flex items-center gap-2 p-2 hover:bg-gray-600 rounded cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={column.selected}
                  onChange={() => onToggleColumn(column.name)}
                  className="rounded"
                />
                <Columns className="w-3 h-3 text-gray-400" />
                <span className="text-sm flex-1">{column.name}</span>
                <span className="text-xs text-gray-400">{column.type}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const JoinCard: React.FC<{ join: Join; onRemove: () => void }> = ({
  join,
  onRemove,
}) => (
  <div className="bg-gray-700 p-3 rounded-lg flex items-center justify-between hover:bg-gray-600 transition-colors">
    <div className="flex items-center gap-3">
      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-semibold">
        {join.type}
      </span>
      <span className="text-sm font-mono">
        {join.leftTable}.{join.leftColumn}
      </span>
      <ArrowRight className="w-4 h-4 text-gray-400" />
      <span className="text-sm font-mono">
        {join.rightTable}.{join.rightColumn}
      </span>
    </div>
    <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

const ConditionCard: React.FC<{
  condition: Condition;
  onRemove: () => void;
}> = ({ condition, onRemove }) => (
  <div className="bg-gray-700 p-3 rounded-lg flex items-center justify-between hover:bg-gray-600 transition-colors">
    <div className="flex items-center gap-3">
      {condition.logical && (
        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-semibold">
          {condition.logical}
        </span>
      )}
      <span className="text-sm font-mono">
        {condition.column} {condition.operator} {condition.value}
      </span>
    </div>
    <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

const TableSelectorDialog: React.FC<{
  tables: string[];
  onSelect: (table: string) => void;
  onClose: () => void;
}> = ({ tables, onSelect, onClose }) => (
  <div
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    onClick={onClose}
  >
    <div
      className="bg-gray-800 rounded-lg p-6 w-full max-w-md"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-xl font-bold mb-4">Select Table</h3>
      {tables.length === 0 ? (
        <div className="text-center py-8">
          <Database className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400 mb-2">No tables available</p>
          <p className="text-sm text-gray-500">
            Make sure your database connection is active and has tables
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 text-sm text-gray-400">
            {tables.length} table{tables.length !== 1 ? "s" : ""} available
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {tables.map((table) => (
              <button
                key={table}
                onClick={() => onSelect(table)}
                className="w-full text-left p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Table className="w-4 h-4 text-blue-400" />
                  <span className="font-medium">{table}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          {tables.length === 0 ? "Close" : "Cancel"}
        </Button>
      </div>
    </div>
  </div>
);

const JoinDialog: React.FC<{
  tables: TableNode[];
  onAdd: (join: Omit<Join, "id">) => void;
  onClose: () => void;
}> = ({ tables, onAdd, onClose }) => {
  const [leftTable, setLeftTable] = useState("");
  const [rightTable, setRightTable] = useState("");
  const [leftColumn, setLeftColumn] = useState("");
  const [rightColumn, setRightColumn] = useState("");
  const [joinType, setJoinType] = useState<Join["type"]>("INNER");

  const handleSubmit = () => {
    if (!leftTable || !rightTable || !leftColumn || !rightColumn) {
      toast.error("Please fill all fields");
      return;
    }
    onAdd({ leftTable, rightTable, leftColumn, rightColumn, type: joinType });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Join</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Join Type</label>
            <select
              value={joinType}
              onChange={(e) => setJoinType(e.target.value as Join["type"])}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="INNER">INNER JOIN</option>
              <option value="LEFT">LEFT JOIN</option>
              <option value="RIGHT">RIGHT JOIN</option>
              <option value="FULL">FULL JOIN</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1">Left Table</label>
              <select
                value={leftTable}
                onChange={(e) => setLeftTable(e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">Select...</option>
                {tables.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Left Column</label>
              <input
                type="text"
                value={leftColumn}
                onChange={(e) => setLeftColumn(e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="column"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1">Right Table</label>
              <select
                value={rightTable}
                onChange={(e) => setRightTable(e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">Select...</option>
                {tables.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Right Column</label>
              <input
                type="text"
                value={rightColumn}
                onChange={(e) => setRightColumn(e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="column"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add Join
          </Button>
        </div>
      </div>
    </div>
  );
};

const ConditionDialog: React.FC<{
  tables: TableNode[];
  onAdd: (condition: Omit<Condition, "id">) => void;
  onClose: () => void;
}> = ({ tables, onAdd, onClose }) => {
  const [column, setColumn] = useState("");
  const [operator, setOperator] = useState("=");
  const [value, setValue] = useState("");
  const [logical, setLogical] = useState<"AND" | "OR">("AND");

  // Get all available columns from all tables
  const availableColumns = tables.flatMap((table) =>
    table.columns.map((col) => ({
      value: `${table.alias || table.name}.${col.name}`,
      label: `${table.name}.${col.name} (${col.type})`,
      tableName: table.name,
    }))
  );

  const handleSubmit = () => {
    if (!column || !value) {
      toast.error("Please fill all fields");
      return;
    }
    onAdd({ column, operator, value, logical });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Condition</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Column</label>
            <select
              value={column}
              onChange={(e) => setColumn(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Select column...</option>
              {availableColumns.map((col, idx) => (
                <option key={idx} value={col.value}>
                  {col.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Choose a column to filter on
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1">Operator</label>
              <select
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="=">=</option>
                <option value="!=">!=</option>
                <option value=">">{">"}</option>
                <option value="<">{"<"}</option>
                <option value=">=">{">="}</option>
                <option value="<=">{"<="}</option>
                <option value="LIKE">LIKE</option>
                <option value="IN">IN</option>
                <option value="IS NULL">IS NULL</option>
                <option value="IS NOT NULL">IS NOT NULL</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Logical Operator</label>
              <select
                value={logical}
                onChange={(e) => setLogical(e.target.value as "AND" | "OR")}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="AND">AND</option>
                <option value="OR">OR</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Value</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="value or 'text'"
            />
            <p className="text-xs text-gray-400 mt-1">
              Tip: Use quotes for text values (e.g., 'John'), numbers without
              quotes
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add Condition
          </Button>
        </div>
      </div>
    </div>
  );
};

const OrderByDialog: React.FC<{
  tables: TableNode[];
  onAdd: (orderBy: Omit<OrderBy, "id">) => void;
  onClose: () => void;
}> = ({ tables, onAdd, onClose }) => {
  const [column, setColumn] = useState("");
  const [direction, setDirection] = useState<"ASC" | "DESC">("ASC");

  // Get all available columns from all tables
  const availableColumns = tables.flatMap((table) =>
    table.columns.map((col) => ({
      value: `${table.alias || table.name}.${col.name}`,
      label: `${table.name}.${col.name} (${col.type})`,
      tableName: table.name,
    }))
  );

  const handleSubmit = () => {
    if (!column) {
      toast.error("Please select a column");
      return;
    }
    onAdd({ column, direction });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Order By</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Column</label>
            <select
              value={column}
              onChange={(e) => setColumn(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Select column...</option>
              {availableColumns.map((col, idx) => (
                <option key={idx} value={col.value}>
                  {col.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Choose a column to sort by
            </p>
          </div>

          <div>
            <label className="block text-sm mb-1">Direction</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as "ASC" | "DESC")}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="ASC">Ascending (ASC)</option>
              <option value="DESC">Descending (DESC)</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

const GroupByDialog: React.FC<{
  tables: TableNode[];
  onAdd: (groupBy: Omit<GroupBy, "id">) => void;
  onClose: () => void;
}> = ({ tables, onAdd, onClose }) => {
  const [column, setColumn] = useState("");

  // Get all available columns from all tables
  const availableColumns = tables.flatMap((table) =>
    table.columns.map((col) => ({
      value: `${table.alias || table.name}.${col.name}`,
      label: `${table.name}.${col.name} (${col.type})`,
      tableName: table.name,
    }))
  );

  const handleSubmit = () => {
    if (!column) {
      toast.error("Please select a column");
      return;
    }
    onAdd({ column });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Group By</h3>

        <div>
          <label className="block text-sm mb-1">Column</label>
          <select
            value={column}
            onChange={(e) => setColumn(e.target.value)}
            className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Select column...</option>
            {availableColumns.map((col, idx) => (
              <option key={idx} value={col.value}>
                {col.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">
            Group by non-aggregated columns
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

const HavingDialog: React.FC<{
  onAdd: (having: Omit<Having, "id">) => void;
  onClose: () => void;
}> = ({ onAdd, onClose }) => {
  const [aggregation, setAggregation] = useState("");
  const [operator, setOperator] = useState("=");
  const [value, setValue] = useState("");
  const [logical, setLogical] = useState<"AND" | "OR">("AND");

  const handleSubmit = () => {
    if (!aggregation || !value) {
      toast.error("Please fill all fields");
      return;
    }
    onAdd({ aggregation, operator, value, logical });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Having Clause</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Aggregation</label>
            <input
              type="text"
              value={aggregation}
              onChange={(e) => setAggregation(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., COUNT(column), SUM(column)"
            />
            <p className="text-xs text-gray-400 mt-1">
              Enter aggregation function with column name
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1">Operator</label>
              <select
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="=">=</option>
                <option value="!=">!=</option>
                <option value=">">{">"}</option>
                <option value="<">{"<"}</option>
                <option value=">=">{">="}</option>
                <option value="<=">{"<="}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Logical</label>
              <select
                value={logical}
                onChange={(e) => setLogical(e.target.value as "AND" | "OR")}
                className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="AND">AND</option>
                <option value="OR">OR</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Value</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="value"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VisualQueryBuilder;
